import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiDelete, apiGet, apiPatch, apiPost, TENANT_TOKEN_KEY } from '../../api/client';
import { useAgentSelector } from '../../auth/AgentSelectorContext';
import { Button } from '../../design-system';
import { tokens } from '../../design-system/tokens';

type ConfigField = {
  key: string;
  type: 'string' | 'textarea' | 'number' | 'boolean';
  label: string;
  description?: string;
  maxLength?: number;
  required?: boolean;
};

type ConditionField = {
  key: string;
  label: string;
  description?: string;
  type: 'enumMulti' | 'string';
  options?: Array<{ value: string; label: string }>;
  maxLength?: number;
};

type AlertTrigger = {
  type: string;
  label: string;
  description: string;
  triggerSources: string[];
  defaultPriority: number;
  defaultBackgroundColor: string;
  configFields: ConfigField[];
  conditionSchema: ConditionField[];
};

type AlertRule = {
  id: string;
  triggerType: string;
  name: string;
  enabled: boolean;
  basePriority: number;
  backgroundColor: string;
  config: Record<string, unknown>;
  triggerLabel: string;
  triggerDescription: string;
  configFields: ConfigField[];
  conditionSchema?: ConditionField[];
  conditionsSummary?: string | null;
};

type EditorState = {
  mode: 'create' | 'edit';
  ruleId?: string;
  triggerType: string;
  name: string;
  enabled: boolean;
  basePriority: number;
  backgroundColor: string;
  config: Record<string, unknown>;
};

function getConditionsObject(config: Record<string, unknown>): Record<string, unknown> {
  const c = config.conditions;
  if (c && typeof c === 'object' && !Array.isArray(c)) return { ...(c as Record<string, unknown>) };
  return {};
}

function setConditionValue(
  config: Record<string, unknown>,
  key: string,
  value: string[] | string | undefined
): Record<string, unknown> {
  const conditions = getConditionsObject(config);
  if (value === undefined || value === '' || (Array.isArray(value) && value.length === 0)) {
    delete conditions[key];
  } else {
    conditions[key] = value;
  }
  const next = { ...config };
  if (Object.keys(conditions).length === 0) {
    delete next.conditions;
  } else {
    next.conditions = conditions;
  }
  return next;
}

/** IDs de agente en conditions.virtualAgentId (string o array). */
function ruleScopedAgentIds(rule: { config: Record<string, unknown> }): string[] {
  const cond = getConditionsObject(rule.config);
  const v = cond.virtualAgentId;
  if (Array.isArray(v)) {
    return v.filter((x): x is string => typeof x === 'string').map((x) => x.trim()).filter(Boolean);
  }
  if (typeof v === 'string' && v.trim()) return [v.trim()];
  return [];
}

function ruleAppliesToAgent(rule: { config: Record<string, unknown> }, agentId: string | null): boolean {
  if (!agentId) return true;
  const ids = ruleScopedAgentIds(rule);
  if (ids.length === 0) return true;
  return ids.includes(agentId);
}

/** Fuerza el scope al agente seleccionado (regla por defecto). */
function withSelectedAgentScope(
  config: Record<string, unknown>,
  agentId: string | null,
  schema: ConditionField[] | undefined
): Record<string, unknown> {
  if (!agentId) return config;
  const hasAgentField = (schema ?? []).some((f) => f.key === 'virtualAgentId');
  if (!hasAgentField) return config;
  return setConditionValue(config, 'virtualAgentId', agentId);
}

export function NotificationSettingsPage() {
  const token = localStorage.getItem(TENANT_TOKEN_KEY);
  const { selectedAgentId, selectedAgent, agents, loading: agentsLoading } = useAgentSelector();
  const [triggers, setTriggers] = useState<AlertTrigger[]>([]);
  const [rules, setRules] = useState<AlertRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [editor, setEditor] = useState<EditorState | null>(null);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError('');
    try {
      const [trig, ruleData] = await Promise.all([
        apiGet<{ items: AlertTrigger[] }>('/api/console/alert-triggers', token, {
          showErrorGrowl: false,
        }),
        apiGet<{ items: AlertRule[] }>('/api/console/alert-rules', token, { showErrorGrowl: false }),
      ]);
      setTriggers(trig.items);
      setRules(ruleData.items);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al cargar');
      setTriggers([]);
      setRules([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    void load();
  }, [load]);

  const visibleRules = useMemo(
    () => rules.filter((r) => ruleAppliesToAgent(r, selectedAgentId)),
    [rules, selectedAgentId],
  );

  const openCreate = () => {
    const first = triggers[0];
    if (!first) return;
    const config = withSelectedAgentScope({}, selectedAgentId, first.conditionSchema);
    setEditor({
      mode: 'create',
      triggerType: first.type,
      name: first.label,
      enabled: true,
      basePriority: first.defaultPriority,
      backgroundColor: first.defaultBackgroundColor,
      config,
    });
  };

  const openEdit = (rule: AlertRule) => {
    const schema =
      triggers.find((t) => t.type === rule.triggerType)?.conditionSchema ?? rule.conditionSchema;
    setEditor({
      mode: 'edit',
      ruleId: rule.id,
      triggerType: rule.triggerType,
      name: rule.name,
      enabled: rule.enabled,
      basePriority: rule.basePriority,
      backgroundColor: rule.backgroundColor,
      config: withSelectedAgentScope({ ...rule.config }, selectedAgentId, schema),
    });
  };

  const currentTrigger = editor
    ? triggers.find((t) => t.type === editor.triggerType) ?? null
    : null;

  const handleSaveEditor = async () => {
    if (!token || !editor) return;
    if (!selectedAgentId) {
      setError('Seleccioná un agente en el selector de la barra lateral.');
      return;
    }
    setSaving(true);
    setError('');
    const schema = currentTrigger?.conditionSchema ?? [];
    const config = withSelectedAgentScope(editor.config, selectedAgentId, schema);
    try {
      if (editor.mode === 'create') {
        await apiPost(
          '/api/console/alert-rules',
          {
            triggerType: editor.triggerType,
            name: editor.name,
            enabled: editor.enabled,
            basePriority: editor.basePriority,
            backgroundColor: editor.backgroundColor,
            config,
          },
          token,
          { successMessage: 'Regla creada' }
        );
      } else if (editor.ruleId) {
        await apiPatch(
          `/api/console/alert-rules/${editor.ruleId}`,
          {
            name: editor.name,
            enabled: editor.enabled,
            basePriority: editor.basePriority,
            backgroundColor: editor.backgroundColor,
            config,
          },
          token,
          { successMessage: 'Regla actualizada' }
        );
      }
      setEditor(null);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const toggleEnabled = async (rule: AlertRule) => {
    if (!token) return;
    try {
      await apiPatch(
        `/api/console/alert-rules/${rule.id}`,
        { enabled: !rule.enabled },
        token,
        { successMessage: rule.enabled ? 'Regla desactivada' : 'Regla activada' }
      );
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al actualizar');
    }
  };

  const handleDelete = async (rule: AlertRule) => {
    if (!token) return;
    if (!window.confirm(`¿Borrar la regla “${rule.name}”?`)) return;
    try {
      await apiDelete(`/api/console/alert-rules/${rule.id}`, token, {
        successMessage: 'Regla eliminada',
      });
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al borrar');
    }
  };

  const inpStyle = {
    padding: '8px 10px',
    borderRadius: 8,
    border: `1px solid ${tokens.semantic.border}`,
    fontSize: 13,
    width: '100%' as const,
    boxSizing: 'border-box' as const,
  };

  const conditionSchema = currentTrigger?.conditionSchema ?? [];
  const editorConditions = editor ? getConditionsObject(editor.config) : {};

  return (
    <div style={{ maxWidth: 1080, margin: '0 auto', padding: '28px 24px 40px' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 16,
          marginBottom: 24,
        }}
      >
        <div>
          <h1
            style={{
              margin: 0,
              fontSize: 22,
              fontWeight: 800,
              letterSpacing: '-0.02em',
              color: '#0f172a',
            }}
          >
            Configuración de alertas
          </h1>
          <p style={{ margin: '6px 0 0', fontSize: 14, color: '#64748b' }}>
            Reglas del agente{' '}
            <strong style={{ color: '#0f172a' }}>
              {selectedAgent?.name ?? (agentsLoading ? '…' : 'sin seleccionar')}
            </strong>
            . Por defecto aplican solo a ese agente
            {agents.length === 1 ? ' (único agente de la org)' : ''}.
          </p>
        </div>
        <Button
          variant="primary"
          size="md"
          onClick={openCreate}
          disabled={!triggers.length || !selectedAgentId}
        >
          Nueva regla
        </Button>
      </div>

      {error && (
        <div
          style={{
            marginBottom: 16,
            padding: '10px 14px',
            borderRadius: 8,
            background: tokens.colors.error.light,
            color: tokens.colors.error.dark,
            fontSize: 14,
          }}
        >
          {error}
        </div>
      )}

      {loading && <p style={{ color: tokens.semantic.textMuted }}>Cargando…</p>}

      {!loading && visibleRules.length === 0 && !error && (
        <p style={{ color: tokens.semantic.textMuted }}>
          {selectedAgentId
            ? 'No hay reglas para este agente. Creá la primera.'
            : 'Seleccioná un agente en la barra lateral para configurar sus alertas.'}
        </p>
      )}

      {!loading &&
        visibleRules.map((rule) => (
          <div
            key={rule.id}
            style={{
              marginBottom: 16,
              padding: 16,
              borderRadius: 12,
              border: `1px solid ${tokens.semantic.border}`,
              background: rule.backgroundColor,
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: 16,
              }}
            >
              <div style={{ minWidth: 0, flex: 1 }}>
                <div
                  style={{
                    fontWeight: 700,
                    fontSize: 16,
                    color: tokens.semantic.textPrimary,
                    marginBottom: 4,
                  }}
                >
                  {rule.name}
                </div>
                <div style={{ fontSize: 13, color: tokens.semantic.textSecondary, marginBottom: 8 }}>
                  {rule.triggerLabel}
                  {typeof rule.config.replyPrompt === 'string' && rule.config.replyPrompt.trim()
                    ? ' · con prompt de respuesta'
                    : ''}
                  {rule.conditionsSummary ? ` · Solo: ${rule.conditionsSummary}` : ''}
                </div>
                <div
                  style={{
                    fontSize: 11,
                    color: tokens.semantic.textSubtle,
                    fontFamily: 'ui-monospace, monospace',
                  }}
                >
                  {rule.triggerType}
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-end' }}>
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    cursor: 'pointer',
                    fontSize: 14,
                    fontWeight: 600,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={rule.enabled}
                    onChange={() => void toggleEnabled(rule)}
                    style={{ width: 18, height: 18 }}
                  />
                  Activa
                </label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <Button variant="secondary" size="sm" onClick={() => openEdit(rule)}>
                    Editar
                  </Button>
                  <Button variant="secondary" size="sm" onClick={() => void handleDelete(rule)}>
                    Borrar
                  </Button>
                </div>
              </div>
            </div>
          </div>
        ))}

      {editor && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15,23,42,0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 50,
            padding: 16,
          }}
          onClick={() => !saving && setEditor(null)}
        >
          <div
            style={{
              width: '100%',
              maxWidth: 560,
              maxHeight: '90vh',
              overflow: 'auto',
              background: '#fff',
              borderRadius: 16,
              padding: 24,
              boxShadow: '0 20px 50px rgba(0,0,0,0.2)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ margin: '0 0 16px', fontSize: 18, fontWeight: 800 }}>
              {editor.mode === 'create' ? 'Nueva regla de alerta' : 'Editar regla'}
            </h2>

            {editor.mode === 'create' && (
              <label style={{ display: 'block', marginBottom: 12, fontSize: 13, fontWeight: 600 }}>
                Disparador
                <select
                  style={{ ...inpStyle, marginTop: 6 }}
                  value={editor.triggerType}
                  onChange={(e) => {
                    const t = triggers.find((x) => x.type === e.target.value);
                    setEditor({
                      ...editor,
                      triggerType: e.target.value,
                      name: t?.label ?? editor.name,
                      basePriority: t?.defaultPriority ?? editor.basePriority,
                      backgroundColor: t?.defaultBackgroundColor ?? editor.backgroundColor,
                      config: withSelectedAgentScope({}, selectedAgentId, t?.conditionSchema),
                    });
                  }}
                >
                  {triggers.map((t) => (
                    <option key={t.type} value={t.type}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </label>
            )}

            {currentTrigger && (
              <p style={{ fontSize: 13, color: '#64748b', marginTop: 0, marginBottom: 16 }}>
                {currentTrigger.description}
              </p>
            )}

            <label style={{ display: 'block', marginBottom: 12, fontSize: 13, fontWeight: 600 }}>
              Nombre
              <input
                style={{ ...inpStyle, marginTop: 6 }}
                value={editor.name}
                onChange={(e) => setEditor({ ...editor, name: e.target.value })}
              />
            </label>

            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                marginBottom: 12,
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              <input
                type="checkbox"
                checked={editor.enabled}
                onChange={(e) => setEditor({ ...editor, enabled: e.target.checked })}
              />
              Activa
            </label>

            <label style={{ display: 'block', marginBottom: 12, fontSize: 13, fontWeight: 600 }}>
              Prioridad (0–100)
              <input
                type="number"
                min={0}
                max={100}
                style={{ ...inpStyle, marginTop: 6 }}
                value={editor.basePriority}
                onChange={(e) =>
                  setEditor({ ...editor, basePriority: Number(e.target.value) || 0 })
                }
              />
            </label>

            <label style={{ display: 'block', marginBottom: 12, fontSize: 13, fontWeight: 600 }}>
              Color de fondo
              <div style={{ display: 'flex', gap: 8, marginTop: 6, alignItems: 'center' }}>
                <input
                  type="color"
                  value={editor.backgroundColor}
                  onChange={(e) => setEditor({ ...editor, backgroundColor: e.target.value })}
                  style={{ width: 44, height: 36 }}
                />
                <input
                  style={inpStyle}
                  value={editor.backgroundColor}
                  onChange={(e) => {
                    const v = e.target.value.trim();
                    if (/^#[0-9a-fA-F]{0,6}$/.test(v)) {
                      setEditor({ ...editor, backgroundColor: v });
                    }
                  }}
                />
              </div>
            </label>

            {conditionSchema.length > 0 && (
              <div
                style={{
                  marginBottom: 16,
                  padding: 14,
                  borderRadius: 10,
                  border: `1px solid ${tokens.semantic.border}`,
                  background: '#f8fafc',
                }}
              >
                <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4 }}>Condiciones</div>
                <p style={{ margin: '0 0 12px', fontSize: 12, color: '#64748b' }}>
                  La regla queda ligada al agente del selector. Otras condiciones son opcionales.
                </p>
                {conditionSchema.map((field) => {
                  if (field.key === 'virtualAgentId') {
                    return (
                      <div key={field.key} style={{ marginBottom: 12 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
                          {field.label}
                        </div>
                        <div
                          style={{
                            ...inpStyle,
                            background: '#eef2ff',
                            color: '#3730a3',
                            fontWeight: 600,
                          }}
                        >
                          {selectedAgent?.name ?? 'Agente seleccionado'}
                          {agents.length === 1 ? ' · único agente' : ''}
                        </div>
                        <div style={{ fontSize: 12, color: '#64748b', marginTop: 6 }}>
                          Por defecto aplica solo a este agente. Cambiá el selector de la barra
                          lateral para configurar otro.
                        </div>
                      </div>
                    );
                  }
                  if (field.type === 'enumMulti') {
                    const selected = Array.isArray(editorConditions[field.key])
                      ? (editorConditions[field.key] as string[])
                      : [];
                    return (
                      <div key={field.key} style={{ marginBottom: 12 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
                          {field.label}
                        </div>
                        {field.description && (
                          <div style={{ fontSize: 12, color: '#64748b', marginBottom: 8 }}>
                            {field.description}
                          </div>
                        )}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          {(field.options ?? []).map((opt) => {
                            const checked = selected.includes(opt.value);
                            return (
                              <label
                                key={opt.value}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 8,
                                  fontSize: 13,
                                  fontWeight: 500,
                                  cursor: 'pointer',
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() => {
                                    const next = checked
                                      ? selected.filter((v) => v !== opt.value)
                                      : [...selected, opt.value];
                                    setEditor({
                                      ...editor,
                                      config: setConditionValue(editor.config, field.key, next),
                                    });
                                  }}
                                />
                                {opt.label}
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    );
                  }
                  const strVal = Array.isArray(editorConditions[field.key])
                    ? String((editorConditions[field.key] as string[])[0] ?? '')
                    : typeof editorConditions[field.key] === 'string'
                      ? (editorConditions[field.key] as string)
                      : '';
                  return (
                    <label
                      key={field.key}
                      style={{ display: 'block', marginBottom: 12, fontSize: 13, fontWeight: 600 }}
                    >
                      {field.label}
                      {field.description && (
                        <div
                          style={{ fontWeight: 400, color: '#64748b', margin: '4px 0 6px', fontSize: 12 }}
                        >
                          {field.description}
                        </div>
                      )}
                      <input
                        style={inpStyle}
                        maxLength={field.maxLength}
                        value={strVal}
                        placeholder="Opcional"
                        onChange={(e) =>
                          setEditor({
                            ...editor,
                            config: setConditionValue(
                              editor.config,
                              field.key,
                              e.target.value.trim() ? e.target.value.trim() : undefined
                            ),
                          })
                        }
                      />
                    </label>
                  );
                })}
              </div>
            )}

            {(currentTrigger?.configFields ?? []).map((field) => (
              <label
                key={field.key}
                style={{ display: 'block', marginBottom: 12, fontSize: 13, fontWeight: 600 }}
              >
                {field.label}
                {field.description && (
                  <div style={{ fontWeight: 400, color: '#64748b', margin: '4px 0 6px', fontSize: 12 }}>
                    {field.description}
                  </div>
                )}
                {field.type === 'textarea' ? (
                  <textarea
                    style={{ ...inpStyle, minHeight: 120, resize: 'vertical' as const }}
                    maxLength={field.maxLength}
                    value={
                      typeof editor.config[field.key] === 'string'
                        ? (editor.config[field.key] as string)
                        : ''
                    }
                    onChange={(e) =>
                      setEditor({
                        ...editor,
                        config: { ...editor.config, [field.key]: e.target.value },
                      })
                    }
                  />
                ) : (
                  <input
                    style={{ ...inpStyle, marginTop: field.description ? 0 : 6 }}
                    maxLength={field.maxLength}
                    value={
                      typeof editor.config[field.key] === 'string'
                        ? (editor.config[field.key] as string)
                        : ''
                    }
                    onChange={(e) =>
                      setEditor({
                        ...editor,
                        config: { ...editor.config, [field.key]: e.target.value },
                      })
                    }
                  />
                )}
              </label>
            ))}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
              <Button variant="secondary" size="md" onClick={() => setEditor(null)} disabled={saving}>
                Cancelar
              </Button>
              <Button variant="primary" size="md" onClick={() => void handleSaveEditor()} disabled={saving}>
                {saving ? 'Guardando…' : 'Guardar'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
